<template>
  <div
    class="login_background flex justify-center align-center flex-col items-center h-screen w-full bg-no-repeat bg-right"
    :style="{ backgroundImage: `url(${require('images/login_background.png')})` }"
  >
    <!-- Custom loading: show when generating content (before Get the App step) -->
    <SignUpContentLoading
      v-if="loading"
      :step1-done="loadingStep1Done"
      :step2-done="loadingStep2Done"
      :step3-done="loadingStep3Done"
      :build-type="selectedBuildType"
      :build-subtype="selectedSubtype"
    />

    <template v-else>
      <div class="flex justify-start flex-col" style="width: 500px">
        <div class="self-center mb-10">
          <logoSvg />
        </div>
        <ProgressBar></ProgressBar>
        <div
          class="w-4 h-0.5 flex-shrink-0 bg-coach-green-500 my-4 self-center"
        ></div>
        <div class="info-form-div">
          <div class="main-body">
            <!-- Step 1: Primary Selection -->
            <div v-if="!selectedBuildType">
            <h2 class="text-2xl font-medium text-black text-center mb-6">
              What do you want to build first?
            </h2>
            <div class="flex flex-col gap-4">
              <div
                v-for="option in buildOptions"
                :key="option.value"
                @click="selectBuildType(option.value)"
                class="build-option-card cursor-pointer p-4 rounded-lg border-2 border-gray-200 hover:border-coach-green-500 transition-all"
              >
                <div class="flex items-center gap-4">
                  <div class="text-3xl">{{ option.icon }}</div>
                  <div>
                    <div class="font-medium text-lg text-black">{{ option.label }}</div>
                    <div class="text-gray-500 text-sm">{{ option.description }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 2: Conditional Follow-up -->
          <div v-else>
            <h2 class="text-2xl font-medium text-black text-center mb-6">
              {{ followUpQuestion }}
            </h2>
            <div class="flex flex-col gap-4">
              <div
                v-for="option in followUpOptions"
                :key="option.value"
                @click="selectSubtype(option.value)"
                class="build-option-card cursor-pointer p-4 rounded-lg border-2 border-gray-200 hover:border-coach-green-500 transition-all"
                :class="{ 'border-coach-green-500 bg-green-50': selectedSubtype === option.value }"
              >
                <div class="flex items-center gap-4">
                  <div class="font-medium text-lg text-black">{{ option.label }}</div>
                </div>
              </div>
            </div>

            <div class="business-footer-div gap-4 mt-8">
              <div
                @click="goBack"
                class="w-40 h-11 p-3 bg-coach-green-500/5 rounded-lg inline-flex justify-center items-center cursor-pointer"
                style="background-color: rgba(58, 181, 116, 0.05)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M7.76579 13.3657C7.45337 13.6781 6.94683 13.6781 6.63441 13.3657L1.83441 8.56571C1.52199 8.25329 1.52199 7.74676 1.83441 7.43434L6.63442 2.63434C6.94683 2.32192 7.45337 2.32192 7.76579 2.63434C8.07821 2.94676 8.07821 3.45329 7.76579 3.76571L4.33147 7.20002H13.6001C14.0419 7.20002 14.4001 7.5582 14.4001 8.00003C14.4001 8.44185 14.0419 8.80003 13.6001 8.80003L4.33147 8.80002L7.76579 12.2343C8.07821 12.5468 8.07821 13.0533 7.76579 13.3657Z"
                    fill="#3AB574"
                  />
                </svg>
                <div class="px-2 text-base font-medium" style="color: #3ab574">
                  Back
                </div>
              </div>
              <input
                type="button"
                @click="submit"
                :value="loading ? 'Building...' : 'Build It'"
                :disabled="!selectedSubtype || loading"
                class="btn button next-btn flex-grow"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    </template>
  </div>
</template>

<script>
import ApiUrls from '../../api-url';
import { mapState } from 'vuex';
import getters from '../../stores/login-store/getters/login-getters';
import AuthenticationMixin from '../../authentication-mixin';
import ProgressBar from '../shared/progress-bar';
import logoSvg from '../../svgs/logoSvg.vue';
import SignUpContentLoading from './sign-up-content-loading.vue';

export default {
  components: { ProgressBar, logoSvg, SignUpContentLoading },
  mixins: [ApiUrls, AuthenticationMixin],
  data() {
    return {
      selectedBuildType: null,
      selectedSubtype: null,
      loading: false,
      loadingStep1Done: false,
      loadingStep2Done: false,
      loadingStep3Done: false,
      loadingMinTimeElapsed: false,
      loadingApiResult: null,
      buildOptions: [
        {
          value: 'workout',
          label: 'A workout program',
          description: 'Create structured training for your clients',
          icon: '💪',
        },
        {
          value: 'nutrition',
          label: 'A nutrition program',
          description: 'Build nutrition habits or teach clients to track macros',
          icon: '🥗',
        },
        {
          value: 'challenge',
          label: 'A challenge',
          description: 'Launch an engaging fitness challenge',
          icon: '🏆',
        },
      ],
      workoutOptions: [
        { value: 'functional_strength', label: 'Functional strength (general population)' },
        { value: 'bodybuilding', label: 'Bodybuilding' },
        { value: 'powerlifting', label: 'Powerlifting' },
        { value: 'crossfit', label: 'CrossFit' },
      ],
      nutritionOptions: [
        { value: 'habit_based', label: 'Habit-based coaching' },
        { value: 'macro_tracking', label: 'Macro tracking' },
      ],
      challengeOptions: [
        { value: '7_day', label: '7-day quick start' },
        { value: '4_week', label: '4-week transformation' },
        { value: '6_week', label: '6-week intensive' },
      ],
    };
  },
  beforeCreate: function() {
    document.body.style.backgroundColor = '#FFFFFF';
  },
  async created() {
    try {
      const { data } = await this.axiosInstance.get(this.organizationUrl + 'get_signup_info');
      if (data.success && data.result) {
        const r = data.result;
        if (r.signup_build_type) this.selectedBuildType = r.signup_build_type;
        if (r.signup_build_subtype) this.selectedSubtype = r.signup_build_subtype;
      }
    } catch (error) {
      // Keep default (null) on error
    }
  },
  computed: {
    ...mapState(['userForm']),
    followUpQuestion() {
      switch (this.selectedBuildType) {
        case 'workout':
          return 'What type of workout program?';
        case 'nutrition':
          return 'What type of nutrition plan?';
        case 'challenge':
          return 'What length challenge?';
        default:
          return '';
      }
    },
    followUpOptions() {
      switch (this.selectedBuildType) {
        case 'workout':
          return this.workoutOptions;
        case 'nutrition':
          return this.nutritionOptions;
        case 'challenge':
          return this.challengeOptions;
        default:
          return [];
      }
    },
  },
  methods: {
    selectBuildType(type) {
      this.selectedBuildType = type;
      this.selectedSubtype = null;
    },
    selectSubtype(subtype) {
      this.selectedSubtype = subtype;
    },
    goBack() {
      if (this.selectedBuildType) {
        this.selectedBuildType = null;
        this.selectedSubtype = null;
      } else {
        this.$store.commit('setSelectedStep', getters.SIGN_UP_COACH_INFO());
      }
    },
    async submit() {
      if (!this.selectedSubtype) return;

      this.loading = true;
      this.loadingStep1Done = false;
      this.loadingStep2Done = false;
      this.loadingStep3Done = false;
      this.loadingMinTimeElapsed = false;
      this.loadingApiResult = null;

      const MIN_LOADING_MS = 2000;
      const stepDelayMs = 400;

      setTimeout(() => {
        this.loadingMinTimeElapsed = true;
        this.tryFinishLoading();
      }, MIN_LOADING_MS);

      const payload = {
        ...this.userForm,
        signup_build_type: this.selectedBuildType,
        signup_build_subtype: this.selectedSubtype,
      };

      this.axiosInstance
        .post(`${this.organizationUrl}signup_with_content_generation`, payload)
        .then(({ data }) => {
          if (data.success) {
            this.loadingApiResult = data.result;
            this.loadingStep1Done = true;
            setTimeout(() => {
              this.loadingStep2Done = true;
              setTimeout(() => {
                this.loadingStep3Done = true;
                this.tryFinishLoading();
              }, stepDelayMs);
            }, stepDelayMs);
          } else {
            this.loading = false;
            this.$toast.show('Error generating content. Please try again.', this.toasted_options.error);
          }
        })
        .catch(() => {
          this.loading = false;
          this.$toast.show('Error generating content. Please try again.', this.toasted_options.error);
        });
    },
    tryFinishLoading() {
      if (this.loadingMinTimeElapsed && this.loadingApiResult && this.loadingStep3Done) {
        this.$store.commit('setGeneratedContent', this.loadingApiResult);
        this.$store.commit('setSelectedStep', getters.SIGN_UP_DOWNLOAD_APP());
        this.loading = false;
        this.loadingStep1Done = false;
        this.loadingStep2Done = false;
        this.loadingStep3Done = false;
        this.loadingApiResult = null;
        this.loadingMinTimeElapsed = false;
      }
    },
  },
};
</script>

<style scoped>
.build-option-card:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}
</style>
